import { useDroppable } from '@dnd-kit/core';
import type { CrmDealListItem, CrmStage } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { formatMoney } from '@/utils/currency';
import { DealCard } from './DealCard';
import {
  STALE_DAYS,
  daysSinceActivity,
  isPastDue,
  sumByCurrency,
  weightedSumPln,
} from './dealMath';
import { TONE_BG, TONE_RING, TONE_TEXT } from './tones';
import { CLOSED_WINDOW_DAYS } from './useDealBoard';

export type ColumnKind = 'open' | 'won' | 'lost' | 'inactive';

interface KindStyle {
  /** Rail tint — text on the expanded header, text + bg on the collapsed block. */
  text?: string;
  bg?: string;
  /** Drop-target highlight while a card hovers the column. */
  over: string;
  /** Placeholder shown at the bottom of the list (and on a collapsed rail) during a drag. */
  dropKey: string;
}

// The won rail is green and the lost rail red, but only where the colour is
// a tint: the expanded header keeps a plain title so Wygrana and Przegrana
// read as symmetric column names, not one green pill next to one red pill.
const KIND_STYLE: Record<Exclude<ColumnKind, 'inactive'>, KindStyle> = {
  open: { over: 'ring-2 ring-primary/50 bg-primary/5', dropKey: 'crm.deals.board.dropHere' },
  won: {
    text: TONE_TEXT.emerald,
    bg: TONE_BG.emerald,
    over: cn(TONE_RING.emerald, TONE_BG.emerald),
    dropKey: 'crm.deals.board.dropWon',
  },
  lost: {
    text: TONE_TEXT.destructive,
    bg: TONE_BG.destructive,
    over: cn(TONE_RING.destructive, TONE_BG.destructive),
    dropKey: 'crm.deals.board.dropLost',
  },
};

interface Props {
  id: string;
  title: string;
  kind: ColumnKind;
  stagesById: ReadonlyMap<string, CrmStage>;
  deals: CrmDealListItem[];
  now: Date;
  pending: ReadonlySet<string>;
  /** A drag is in progress anywhere on the board — rails expand, placeholders show. */
  dragActive: boolean;
  /** "Zamknięte" toggle — rails show their closed-window list. */
  expanded: boolean;
  /** True when filters hide every deal of a non-empty column. */
  filteredOut: boolean;
  onOpen: (dealId: string) => void;
}

/**
 * `142 500 zł`, optionally ` · ważone 61 200 zł`, plus a muted `(+2 w EUR)`
 * per other currency. Shared by the toolbar and the open-column headers.
 */
export function MoneySummary({
  deals,
  weighted,
  className,
}: {
  deals: CrmDealListItem[];
  weighted?: boolean;
  className?: string;
}) {
  const buckets = sumByCurrency(deals);
  const others = [...buckets.entries()].filter(([currency]) => currency !== 'PLN');
  return (
    <span className={className}>
      {formatMoney(buckets.get('PLN')?.sum ?? 0, 'PLN')}
      {weighted ? (
        <span className="text-muted-foreground font-normal">
          {' · '}
          {t('crm.deals.board.weighted', { sum: formatMoney(weightedSumPln(deals), 'PLN') })}
        </span>
      ) : null}
      {others.map(([currency, bucket]) => (
        <span key={currency} className="text-muted-foreground ml-1 font-normal">
          {t('crm.deals.board.otherCurrency', { count: bucket.count, currency })}
        </span>
      ))}
    </span>
  );
}

/**
 * One pipeline stage. Open stages are full columns; won/lost are collapsed
 * rails that widen while a drag is active (drop target) or when the user asks
 * for closed history. An inactive-stage column is display-only.
 */
export function DealColumn({
  id,
  title,
  kind,
  stagesById,
  deals,
  now,
  pending,
  dragActive,
  expanded,
  filteredOut,
  onOpen,
}: Props) {
  const isRail = kind === 'won' || kind === 'lost';
  const style = kind === 'inactive' ? undefined : KIND_STYLE[kind];
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !style });

  const stale = kind === 'open' ? deals.filter((deal) => daysSinceActivity(deal, now) >= STALE_DAYS).length : 0;
  const overdue = kind === 'open' ? deals.filter((deal) => isPastDue(deal, now)).length : 0;

  // Rail widths: collapsed 3.5 rem, drag target 10 rem, expanded history 18 rem.
  const railWidth = isRail ? (expanded ? 'w-72' : dragActive ? 'w-40' : 'w-14') : 'w-72';
  const showList = !isRail || expanded;

  return (
    <section
      ref={setNodeRef}
      aria-label={title}
      className={cn(
        'bg-card flex max-h-[calc(100vh-13rem)] shrink-0 flex-col rounded-lg border',
        'transition-[width] duration-200 motion-reduce:transition-none',
        railWidth,
        !style && 'border-dashed opacity-70',
        isOver && style?.over,
      )}
    >
      {isRail && !expanded ? (
        <div className={cn('flex flex-1 flex-col items-center justify-center gap-3 rounded-lg p-2', style?.text, style?.bg)}>
          <span className="text-sm font-medium whitespace-nowrap [writing-mode:vertical-rl] rotate-180">
            {title} · {deals.length}
          </span>
          {dragActive && style ? (
            <span className="text-center text-[11px] leading-tight">{t(style.dropKey)}</span>
          ) : null}
        </div>
      ) : (
        <header className="shrink-0 space-y-0.5 border-b px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className={cn('truncate text-sm font-medium', isRail && style?.text)}>{title}</span>
            <Badge variant="secondary">{deals.length}</Badge>
          </div>
          {isRail ? (
            <p className="text-muted-foreground text-[11px]">
              {t('crm.deals.board.last30Days', { days: CLOSED_WINDOW_DAYS })}
            </p>
          ) : null}
          {kind === 'inactive' ? null : (
            <p className="text-xs tabular-nums">
              {kind === 'lost' ? (
                <span className="text-muted-foreground">
                  {t('crm.deals.board.lostSum', {
                    sum: formatMoney(sumByCurrency(deals).get('PLN')?.sum ?? 0, 'PLN'),
                  })}
                </span>
              ) : (
                <MoneySummary deals={deals} weighted={kind === 'open'} className="font-semibold" />
              )}
            </p>
          )}
          {stale > 0 || overdue > 0 ? (
            <p className="flex flex-wrap gap-x-3 text-[11px]">
              {stale > 0 ? (
                <span className={TONE_TEXT.amber}>● {t('crm.deals.board.staleCount', { count: stale })}</span>
              ) : null}
              {overdue > 0 ? (
                <span className={TONE_TEXT.destructive}>● {t('crm.deals.board.overdueCount', { count: overdue })}</span>
              ) : null}
            </p>
          ) : null}
        </header>
      )}

      {showList ? (
        <div className="flex-1 space-y-2 overflow-y-auto overscroll-contain p-2">
          {deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              stage={stagesById.get(deal.stageId)}
              now={now}
              pending={pending.has(deal.id)}
              onOpen={onOpen}
            />
          ))}
          {deals.length === 0 && !dragActive ? (
            <p className="text-muted-foreground px-1 py-3 text-center text-xs">
              {t(filteredOut ? 'crm.deals.board.noMatch' : 'crm.deals.empty')}
            </p>
          ) : null}
          {dragActive && style ? (
            <div
              className={cn(
                'text-muted-foreground flex h-14 items-center justify-center rounded-md border border-dashed text-xs',
                isOver && 'border-primary text-primary',
              )}
            >
              {t(style.dropKey)}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
